import { useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, CircularProgress, Stack } from '@mui/material';
import { useCustomSnackbar, useUuid } from '@procureai/ui-mui';
import { filter, fromPairs, isEmpty, isNil, isNotNil, map, pipe, pluck, reduce, toPairs } from 'ramda';
import { useCallback, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SvbrgExtendedLineItemsCheck } from '@/generated/gql/graphql';
import { ChatActionNext } from '@/legacy/components/ChatStep/ChatActions/ChatActionNext';
import { useChatSession } from '@/legacy/modules';
import { I18nNamespace } from '~/common/types';
import { ChatMessageProps } from '~/common/types/ChatMessage';
import { ChatStep } from '~/components/ChatStep/ChatStep';

import { ExtendedLineItemAccordion } from './components/ExtendedLineItemAccordion';
import { ReasonInput } from './components/ReasonInput';
import { useExtendedLineItemsCheckStep } from './IntakeExtendedLineItemsCheckStep.hooks';
import { INTAKE_EXTENDED_LINE_ITEMS_NEXT_MUTATION } from './IntakeExtendedLineItemsCheckStep.mutation.gql';
import {
  IntakeExtendedLineItemsCheckStepFormData,
  intakeExtendedLineItemsCheckStepFormDataSchema,
  IntakeSelectableExtendedLineItemData,
  lineItemSchema,
} from './IntakeExtendedLineItemsCheckStep.schema';

/**
 * Utility to convert null values in a record to undefined. Taking all other values as-is.
 *
 * @param record - object with key-value pairs that are potentially null
 * @returns record with undefined values instead of null values
 */
const ensureUndefinedValues = pipe<
  [Record<string, unknown>],
  [string, unknown][],
  [string, unknown][],
  Record<string, unknown>
>(
  toPairs,
  map(([key, value]) => [key, isNil(value) ? undefined : value]),
  fromPairs,
);

/**
 * Merges an original line item with a selected item from catalogue/contract.
 *
 * Overwrites specific fields (title, description, pricing, delivery dates, and supplier)
 * with values from the selected item, while preserving all other fields from the original item.
 *
 * @param original - The original extended line item to be updated
 * @param selectedItem - The item selected from catalogue/contract containing updated field values
 * @returns A new merged line item with selected fields overwritten and original fields preserved
 */
const combineItems = (original: SvbrgExtendedLineItemsCheck, selectedItem: IntakeSelectableExtendedLineItemData) => {
  return {
    ...original,
    title: selectedItem.title,
    description: selectedItem.description,
    pricePerUnit: selectedItem.pricePerUnit,
    earlyDeliveryAt: selectedItem?.earlyDeliveryAt ?? original.earlyDeliveryAt,
    lateDeliveryAt: selectedItem?.lateDeliveryAt ?? original.lateDeliveryAt,
    supplierId: selectedItem?.supplierId,
  };
};

export const IntakeExtendedLineItemsCheckStep = ({ role, createdBy }: ChatMessageProps) => {
  const session = useChatSession();
  const { onError } = useCustomSnackbar();
  const { t: tIntake } = useTranslation(I18nNamespace.intake);
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'IntakeExtendedLineItemsCheckStep' });
  const formId = useUuid();

  // GraphQL query and mutation hooks
  const { extendedChecks, loading: queryLoading } = useExtendedLineItemsCheckStep();
  const [mutate, { loading: mutationLoading, error }] = useMutation(INTAKE_EXTENDED_LINE_ITEMS_NEXT_MUTATION);

  // Handle mutation errors
  useEffect(() => {
    if (error) {
      onError(t('submissionError'));
    }
  }, [error, onError, t]);

  const countItemsThatDontNeedSelection = useCallback(() => {
    return extendedChecks
      ? pipe(
          filter<SvbrgExtendedLineItemsCheck>(
            (check) =>
              isNotNil(check.checkResults) &&
              (isEmpty(check.checkResults.catalogueItems) || isEmpty(check.checkResults.contractItems)),
          ),
        )(extendedChecks).length
      : 0;
  }, [extendedChecks]);

  const form = useForm<IntakeExtendedLineItemsCheckStepFormData>({
    mode: 'onTouched',
    resolver: zodResolver(intakeExtendedLineItemsCheckStepFormDataSchema),
    defaultValues: {
      totalCount: extendedChecks ? extendedChecks.length : 0,
      noSelectionNeededCount: countItemsThatDontNeedSelection(),
      items: extendedChecks
        ? pipe(
            pluck('id'),
            reduce((acc, id) => ({ ...acc, [id]: undefined }), {}),
          )(extendedChecks)
        : undefined,
      reasonNotSelect: undefined,
    },
  });

  useEffect(() => {
    if (extendedChecks) {
      form.setValue('noSelectionNeededCount', countItemsThatDontNeedSelection());
      form.setValue('totalCount', extendedChecks.length);
    }
  }, [extendedChecks, form, countItemsThatDontNeedSelection]);

  const submit = async (checks: SvbrgExtendedLineItemsCheck[], data: IntakeExtendedLineItemsCheckStepFormData) => {
    // Prepare line items for submission
    const selectedItems = data.items ?? {};
    const submitableLineItems = checks.map((original) => {
      const selectedItem = selectedItems[original.id];

      const updatedItem = selectedItem ? combineItems(original, selectedItem) : original;

      return lineItemSchema.parse(ensureUndefinedValues(updatedItem));
    });

    // Submit the mutation
    await mutate({
      variables: {
        data: {
          chatSessionId: session.id,
          lineItems: submitableLineItems,
        },
      },
    });
  };

  return (
    <FormProvider {...form}>
      <form
        id={formId}
        onSubmit={form.handleSubmit((data) => extendedChecks && submit(extendedChecks, data))}
        autoComplete="off"
      >
        <ChatStep
          title={tIntake('IntakeChatStep.assistant')}
          role={role}
          createdBy={createdBy}
          message={queryLoading ? t('analyzingMessage') : t('completedMessage')}
          footer={<ChatActionNext formId={formId} disabled={queryLoading || mutationLoading} />}
        >
          <Stack spacing={4}>
            {queryLoading && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}
            {!queryLoading && extendedChecks && (
              <>
                <ExtendedLineItemAccordion formId={formId} checks={extendedChecks} />
                <ReasonInput />
              </>
            )}
          </Stack>
        </ChatStep>
      </form>
    </FormProvider>
  );
};
